import { useState, useEffect, useRef, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { EvalPanel } from "@/components/evals/eval-panel";
import {
  mcpApi,
  type TenantMcp,
  type AgentMcp,
  type McpCatalogueItem,
  type EffectiveToolsResult,
  type DiscoveredMcp,
} from "@/api/mcp";
import { skillsApi, type DiscoveredSkill } from "@/api/skills";
import { agentKeysApi } from "@/api/agent-keys";
import { agentsApi } from "@/api/agents";
import type { Agent } from "@/api/agents";
import type { AgentApiKey } from "@/api/agent-keys";
import { queryKeys } from "@/lib/query-keys";
import {
  Puzzle, Zap, Plus, Trash2, Key, Copy, Check, Cpu, Image,
  FileText, ChevronDown, ChevronRight, Pencil, Save, RefreshCw, Send, Loader2,
  Wrench, Power, Upload, CheckCircle2, XCircle, Globe, Terminal, Radio,
  Lock, Shield, Mail, Search, ArrowUpRight, UserCog, HeartPulse, AlertCircle,
} from "lucide-react";
import { agentSecretsApi, type AgentSecret } from "@/api/agent-secrets";
import { AgentMemoryAccessTab } from "@/components/agent/agent-memory-access-tab";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { modelsApi, mediaApi } from "@/api/models";
import { ModelPicker, type PickerModel } from "@/components/agent/model-picker";
import { apiFetch } from "@/api/client";
import { m365Api } from "@/api/m365";

interface AgentSkill {
  id: string;
  agentId: string;
  tenantSkillId: string;
  enabled: boolean;
  parameterOverrides: Record<string, unknown> | null;
}

interface TenantSkill {
  id: string;
  name: string;
  enabled: boolean;
}

type WorkspaceFileKey = "soulMd" | "userMd" | "identityMd" | "agentsMd";

const WORKSPACE_FILES: { key: WorkspaceFileKey; label: string; filename: string }[] = [
  { key: "soulMd", label: "SOUL.md", filename: "SOUL.md" },
  { key: "userMd", label: "USER.md", filename: "USER.md" },
  { key: "identityMd", label: "IDENTITY.md", filename: "IDENTITY.md" },
  { key: "agentsMd", label: "AGENTS.md", filename: "AGENTS.md" },
];

const transportIcons: Record<string, typeof Globe> = {
  http: Globe,
  sse: Radio,
  stdio: Terminal,
};

export function AgentConfigTab({ agentId }: { agentId: string }) {
  const { tenantId, isTenantAdmin } = useTenant();
  const queryClient = useQueryClient();
  const { data: allAgents = [] } = useAgentsByCompany(tenantId);

  // Fetch agent data for workspace
  const { data: agent } = useQuery({
    queryKey: queryKeys.agents.detail(agentId),
    queryFn: () => agentsApi.get(agentId) as Promise<Agent>,
  });

  // Workspace state
  const workspace = (agent?.metadata as Record<string, unknown>)?.workspace as
    | Record<string, string>
    | undefined;

  const [expandedFiles, setExpandedFiles] = useState<Set<WorkspaceFileKey>>(new Set());
  const [editingFiles, setEditingFiles] = useState<Set<WorkspaceFileKey>>(new Set());
  const [editedContent, setEditedContent] = useState<Partial<Record<WorkspaceFileKey, string>>>({});

  const toggleExpand = (key: WorkspaceFileKey) => {
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleEdit = (key: WorkspaceFileKey) => {
    setEditingFiles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setEditedContent((ec) => { const copy = { ...ec }; delete copy[key]; return copy; });
      } else {
        next.add(key);
        setEditedContent((ec) => ({ ...ec, [key]: workspace?.[key] || "" }));
      }
      return next;
    });
  };

  const saveWorkspaceFile = useMutation({
    mutationFn: (fileKey: WorkspaceFileKey) =>
      agentsApi.updateWorkspace(agentId, { [fileKey]: editedContent[fileKey] }),
    onSuccess: (_data, fileKey) => {
      setEditingFiles((prev) => { const next = new Set(prev); next.delete(fileKey); return next; });
      setEditedContent((ec) => { const copy = { ...ec }; delete copy[fileKey]; return copy; });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId) });
    },
  });

  const regenerateWorkspace = useMutation({
    mutationFn: () => agentsApi.generateWorkspace(agentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId) }),
  });

  const syncWorkspace = useMutation({
    mutationFn: () => agentsApi.syncWorkspace(agentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId) }),
  });

  const pushWorkspace = useMutation({
    mutationFn: () => agentsApi.pushWorkspace(agentId),
  });

  const generateAvatar = useMutation({
    mutationFn: () => agentsApi.generateAvatar(agentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId) }),
  });

  // ─── Custom avatar upload ────────────────────────────────────────────
  // User can click the avatar (or the "Upload" button) to pick an image.
  // We resize client-side to 256x256 so the DB row stays small and we don't
  // need to involve object storage — the resulting data URI is stored
  // directly in agents.avatarUrl, exactly like the AI-generated avatar.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAvatar = useMutation({
    mutationFn: (dataUri: string) => agentsApi.uploadAvatar(agentId, dataUri),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId) }),
  });

  /** Resize a file to a square 256x256 PNG data URI. Uses an offscreen canvas;
   *  centre-crops so rectangular uploads don't squish. */
  async function resizeImageToSquareDataUri(file: File, size = 256): Promise<string> {
    const bitmap = await createImageBitmap(file);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas 2D context unavailable");

      // Centre-crop to square, then draw scaled to the output size.
      const minDim = Math.min(bitmap.width, bitmap.height);
      const sx = (bitmap.width - minDim) / 2;
      const sy = (bitmap.height - minDim) / 2;
      ctx.drawImage(bitmap, sx, sy, minDim, minDim, 0, 0, size, size);

      return canvas.toDataURL("image/png");
    } finally {
      bitmap.close();
    }
  }

  async function handleAvatarFile(file: File) {
    // Rough client-side guards. Server enforces its own caps.
    const MAX_INPUT_BYTES = 8 * 1024 * 1024;
    if (!file.type.startsWith("image/")) {
      uploadAvatar.reset();
      return;
    }
    if (file.size > MAX_INPUT_BYTES) {
      window.alert("Image is too large (max 8MB). Try a smaller one.");
      return;
    }
    try {
      const dataUri = await resizeImageToSquareDataUri(file, 256);
      uploadAvatar.mutate(dataUri);
    } catch (err) {
      window.alert(`Could not process image: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // --- MCP data ---

  const { data: mcpCatalogue = [] } = useQuery({
    queryKey: queryKeys.mcpCatalogue.all,
    queryFn: () => mcpApi.listCatalogue(),
  });

  const { data: tenantMcps = [] } = useQuery({
    queryKey: queryKeys.tenantMcps.byCompany(tenantId),
    queryFn: () => mcpApi.listTenantMcps(tenantId),
  });

  const { data: agentMcps = [] } = useQuery({
    queryKey: queryKeys.agentMcps.byAgent(agentId),
    queryFn: () => mcpApi.listAgentMcps(agentId),
  });

  // Build lookups
  const catalogueMap = new Map(mcpCatalogue.map((c) => [c.id, c]));
  const tenantMcpMap = new Map(tenantMcps.map((t) => [t.id, t]));

  function getTenantMcpCatalogue(tenantMcpId: string): McpCatalogueItem | undefined {
    const tm = tenantMcpMap.get(tenantMcpId);
    if (!tm?.catalogueMcpId) return undefined;
    return catalogueMap.get(tm.catalogueMcpId);
  }

  function getAvailableTools(tenantMcpId: string): string[] {
    const tm = tenantMcpMap.get(tenantMcpId);
    if (!tm?.catalogueMcpId) return [];
    const cat = catalogueMap.get(tm.catalogueMcpId);
    if (!cat?.toolSchemaCache) return [];
    const allTools = Object.keys(cat.toolSchemaCache);
    // Filter by tenant's allowedTools
    if (tm.allowedTools) {
      const tenantSet = new Set(tm.allowedTools);
      return allTools.filter((t) => tenantSet.has(t));
    }
    return allTools;
  }

  function getToolDescription(tenantMcpId: string, toolName: string): string | undefined {
    const tm = tenantMcpMap.get(tenantMcpId);
    if (!tm?.catalogueMcpId) return undefined;
    const cat = catalogueMap.get(tm.catalogueMcpId);
    if (!cat?.toolSchemaCache) return undefined;
    const schema = cat.toolSchemaCache[toolName];
    if (schema && typeof schema === "object" && "description" in schema) {
      return String(schema.description);
    }
    return undefined;
  }

  // --- MCP agent tool selection state ---

  const [expandedMcpId, setExpandedMcpId] = useState<string | null>(null);
  const [agentToolFilter, setAgentToolFilter] = useState<Record<string, Set<string>>>({});

  // Initialize from existing allowedTools
  useEffect(() => {
    const initial: Record<string, Set<string>> = {};
    for (const mcp of agentMcps) {
      if (mcp.allowedTools) {
        initial[mcp.id] = new Set(mcp.allowedTools);
      }
    }
    setAgentToolFilter(initial);
  }, [agentMcps]);

  const [selectedMcp, setSelectedMcp] = useState("");

  const assignMcp = useMutation({
    mutationFn: (tenantMcpId: string) =>
      mcpApi.assignAgentMcp(agentId, { agentId, tenantMcpId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agentMcps.byAgent(agentId) });
      setSelectedMcp("");
    },
  });

  const removeMcp = useMutation({
    mutationFn: (id: string) => mcpApi.removeAgentMcp(agentId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.agentMcps.byAgent(agentId) }),
  });

  const updateAgentMcpTools = useMutation({
    mutationFn: ({ mcpId, allowedTools }: { mcpId: string; allowedTools: string[] | null }) =>
      mcpApi.updateAgentMcp(agentId, mcpId, { allowedTools }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.agentMcps.byAgent(agentId) }),
  });

  const toggleAgentMcp = useMutation({
    mutationFn: ({ mcpId, enabled }: { mcpId: string; enabled: boolean }) =>
      mcpApi.updateAgentMcp(agentId, mcpId, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.agentMcps.byAgent(agentId) }),
  });

  // Sync MCP config to agent
  const syncMutation = useMutation({
    mutationFn: () => mcpApi.syncToAgent(agentId),
  });

  // Re-probe the upstream MCP server for its current tool list. Caches the
  // discovered tools on the catalogue row so every tenant/agent that references
  // this catalogue MCP sees the refreshed toolset. Scoped per agent-MCP id so
  // the spinner only shows on the row that was clicked.
  const [discoveringToolsFor, setDiscoveringToolsFor] = useState<string | null>(null);
  const [toolDiscoveryResult, setToolDiscoveryResult] = useState<
    Record<string, { success: boolean; count?: number; error?: string }>
  >({});
  const discoverToolsForAgentMcp = useMutation({
    mutationFn: async ({ agentMcpId, catalogueId }: { agentMcpId: string; catalogueId: string }) => {
      const cat = catalogueMap.get(catalogueId);
      if (!cat) throw new Error("Catalogue entry not found");
      const result = await mcpApi.test({
        mcpId: cat.id,
        transportType: cat.transportType,
        url: cat.httpConfig?.url,
        headers: cat.httpConfig?.headers,
        auth: cat.httpConfig?.auth,
      });
      if (result.success && result.tools && result.tools.length > 0) {
        await mcpApi.refreshSchema(cat.id, result.tools);
      }
      return { agentMcpId, result };
    },
    onMutate: ({ agentMcpId }) => {
      setDiscoveringToolsFor(agentMcpId);
    },
    onSuccess: ({ agentMcpId, result }) => {
      setToolDiscoveryResult((prev) => ({
        ...prev,
        [agentMcpId]: result.success
          ? { success: true, count: result.tools?.length ?? 0 }
          : { success: false, error: result.error },
      }));
      queryClient.invalidateQueries({ queryKey: queryKeys.mcpCatalogue.all });
    },
    onError: (err, { agentMcpId }) => {
      setToolDiscoveryResult((prev) => ({
        ...prev,
        [agentMcpId]: { success: false, error: err instanceof Error ? err.message : String(err) },
      }));
    },
    onSettled: () => setDiscoveringToolsFor(null),
  });

  // --- Discovered MCPs ---
  const { data: discoveredMcps = [] } = useQuery({
    queryKey: ["discovered-mcps", agentId],
    queryFn: () => mcpApi.listDiscovered(agentId),
  });

  const discoverMutation = useMutation({
    mutationFn: () => mcpApi.discoverFromAgent(agentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["discovered-mcps", agentId] }),
  });

  const removeDiscoveredMcp = useMutation({
    mutationFn: (id: string) => mcpApi.removeDiscovered(agentId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["discovered-mcps", agentId] }),
  });

  const [promoteTarget, setPromoteTarget] = useState<DiscoveredMcp | null>(null);
  const [promoteAgentIds, setPromoteAgentIds] = useState<Set<string>>(new Set());

  const promoteMutation = useMutation({
    mutationFn: ({ id, targetAgentIds }: { id: string; targetAgentIds?: string[] }) =>
      mcpApi.promoteDiscovered(agentId, id, targetAgentIds ? { targetAgentIds } : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["discovered-mcps", agentId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.agentMcps.byAgent(agentId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantMcps.byCompany(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.mcpCatalogue.all });
      setPromoteTarget(null);
      setPromoteAgentIds(new Set());
    },
  });

  function toggleAgentTool(mcpId: string, toolName: string, availableTools: string[]) {
    setAgentToolFilter((prev) => {
      const current = prev[mcpId] || new Set(availableTools);
      const next = new Set(current);
      if (next.has(toolName)) next.delete(toolName); else next.add(toolName);
      return { ...prev, [mcpId]: next };
    });
  }

  function saveAgentToolFilter(mcpId: string, availableTools: string[]) {
    const filter = agentToolFilter[mcpId];
    if (!filter || filter.size === availableTools.length) {
      updateAgentMcpTools.mutate({ mcpId, allowedTools: null });
    } else {
      updateAgentMcpTools.mutate({ mcpId, allowedTools: Array.from(filter) });
    }
  }

  // Available = tenant pool minus already assigned
  const assignedMcpIds = new Set(agentMcps.map((a) => a.tenantMcpId));
  const availableMcps = tenantMcps.filter((t) => t.enabled && !assignedMcpIds.has(t.id));

  // --- Skills ---

  const { data: tenantSkills = [] } = useQuery({
    queryKey: queryKeys.tenantSkills.byCompany(tenantId),
    queryFn: () => skillsApi.listTenantSkills(tenantId) as Promise<TenantSkill[]>,
  });
  const { data: agentSkills = [] } = useQuery({
    queryKey: queryKeys.agentSkills.byAgent(agentId),
    queryFn: () => skillsApi.listAgentSkills(agentId) as Promise<AgentSkill[]>,
  });

  const [selectedSkill, setSelectedSkill] = useState("");
  const assignSkill = useMutation({
    mutationFn: (tenantSkillId: string) =>
      skillsApi.assignAgentSkill(agentId, { agentId, tenantSkillId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agentSkills.byAgent(agentId) });
      setSelectedSkill("");
    },
  });
  const removeSkill = useMutation({
    mutationFn: (id: string) => skillsApi.removeAgentSkill(agentId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.agentSkills.byAgent(agentId) }),
  });

  const assignedSkillIds = new Set(agentSkills.map((a) => a.tenantSkillId));
  const availableSkills = tenantSkills.filter((t) => t.enabled && !assignedSkillIds.has(t.id));
  const mcpNameMap = new Map(tenantMcps.map((t) => [t.id, t.name]));
  const skillNameMap = new Map(tenantSkills.map((t) => [t.id, t.name]));

  // --- Discovered Skills ---
  const { data: discoveredSkills = [] } = useQuery({
    queryKey: ["discovered-skills", agentId],
    queryFn: () => skillsApi.listDiscovered(agentId),
  });

  const discoverSkillsMutation = useMutation({
    mutationFn: () => skillsApi.discoverFromAgent(agentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["discovered-skills", agentId] }),
  });

  const removeDiscoveredSkill = useMutation({
    mutationFn: (id: string) => skillsApi.removeDiscovered(agentId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["discovered-skills", agentId] }),
  });

  const [promoteSkillTarget, setPromoteSkillTarget] = useState<DiscoveredSkill | null>(null);
  const [promoteSkillAgentIds, setPromoteSkillAgentIds] = useState<Set<string>>(new Set());

  const promoteSkillMutation = useMutation({
    mutationFn: ({ id, targetAgentIds }: { id: string; targetAgentIds?: string[] }) =>
      skillsApi.promoteDiscovered(agentId, id, targetAgentIds ? { targetAgentIds } : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["discovered-skills", agentId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.agentSkills.byAgent(agentId) });
      setPromoteSkillTarget(null);
      setPromoteSkillAgentIds(new Set());
    },
  });

  // ---- API Keys ----
  const { data: apiKeys = [] } = useQuery({
    queryKey: queryKeys.agentKeys.byAgent(agentId),
    queryFn: () => agentKeysApi.list(agentId) as Promise<AgentApiKey[]>,
  });

  const [newKeyName, setNewKeyName] = useState("");
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createKey = useMutation({
    mutationFn: (name: string) => agentKeysApi.create(agentId, name),
    onSuccess: (data) => {
      setCreatedToken(data.token);
      setNewKeyName("");
      setCopied(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.agentKeys.byAgent(agentId) });
    },
  });

  const revokeKey = useMutation({
    mutationFn: (keyId: string) => agentKeysApi.revoke(agentId, keyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.agentKeys.byAgent(agentId) }),
  });

  function handleCopyToken() {
    if (createdToken) {
      navigator.clipboard.writeText(createdToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  // --- Model selection with availability ---
  // Text models only: the endpoint defaults to modality=text, so image and
  // realtime models never reach the conversational-model dropdowns below.
  const { data: modelAvailability } = useQuery({
    queryKey: ["model-availability", tenantId],
    queryFn: () => modelsApi.getAvailability(tenantId),
  });

  const enrichedModels = modelAvailability?.models || [];
  const providers = modelAvailability?.providers || {};

  const currentModel = (agent?.adapterConfig?.model as string) || "";
  const [selectedModel, setSelectedModel] = useState("");

  useEffect(() => {
    if (currentModel) setSelectedModel(currentModel);
  }, [currentModel]);

  // Per-agent backup model — overrides the platform default backup. "" = use default.
  const currentBackup = (agent?.adapterConfig?.fallbackModel as string) || "";
  const [selectedBackup, setSelectedBackup] = useState("");
  useEffect(() => {
    setSelectedBackup(currentBackup);
  }, [currentBackup]);

  const syncModelMutation = useMutation({
    mutationFn: (modelId: string) => modelsApi.syncModel(agentId, modelId, selectedBackup || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId) });
    },
  });

  // --- Image model ---
  // Separate from the model sync above because nothing is pushed to the VM:
  // the platform resolves this per call in the proclaw-media MCP and holds the
  // credential, so saving takes effect immediately with no restart.
  const { data: imageModelData } = useQuery({
    queryKey: ["image-models", tenantId, agentId],
    queryFn: () => mediaApi.imageModels(tenantId, agentId),
  });
  const imageModels = imageModelData?.models || [];
  const resolvedImageModel = imageModelData?.resolved || null;

  const currentImageModel = (agent?.adapterConfig?.imageModel as string) || "";
  const [selectedImageModel, setSelectedImageModel] = useState("");
  useEffect(() => {
    setSelectedImageModel(currentImageModel);
  }, [currentImageModel]);

  const imageModelMutation = useMutation({
    mutationFn: () =>
      modelsApi.setAgentMediaModels(agentId, { imageModel: selectedImageModel || "" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId) });
      queryClient.invalidateQueries({ queryKey: ["image-models", tenantId, agentId] });
    },
  });

  // The Model card saves three choices through one button in its header.
  // Tracked separately because they go to two different endpoints.
  const modelSelectionDirty =
    selectedModel !== currentModel || selectedBackup !== currentBackup;
  const imageModelDirty = selectedImageModel !== currentImageModel;
  const modelCardDirty = modelSelectionDirty || imageModelDirty;
  const modelCardSaving = syncModelMutation.isPending || imageModelMutation.isPending;

  // Image models shaped for the same picker the other two fields use, so all
  // three read as one control rather than two custom fields and a raw select.
  const imageModelsByProvider = imageModels.reduce<Record<string, PickerModel[]>>((acc, m) => {
    const key = m.provider;
    if (!acc[key]) acc[key] = [];
    acc[key].push({
      id: m.id,
      modelId: m.modelId,
      displayName: m.displayName,
      provider: m.provider,
      // Everything this endpoint returns is already granted to the tenant.
      accessReady: true,
    });
    return acc;
  }, {});

  // ── Heartbeat config (Native agents only) ───────────────────────
  // Stored on agent.adapterConfig.heartbeat. The platform-side scheduler
  // ([heartbeat-scheduler.ts]) polls Native agents whose heartbeat.enabled
  // is true and POSTs to the agent's gateway with the configured model.
  const heartbeatCfg = (agent?.adapterConfig as Record<string, unknown> | undefined)?.heartbeat as
    | { enabled?: boolean; intervalMinutes?: number; model?: string; prompt?: string }
    | undefined;
  const isNativeAgent = agent?.adapterType === "anthropic_native";

  const [hbEnabled, setHbEnabled] = useState(false);
  const [hbInterval, setHbInterval] = useState(30);
  const [hbModel, setHbModel] = useState("");
  const [hbPrompt, setHbPrompt] = useState("");

  useEffect(() => {
    setHbEnabled(heartbeatCfg?.enabled === true);
    setHbInterval(typeof heartbeatCfg?.intervalMinutes === "number" ? heartbeatCfg.intervalMinutes : 30);
    setHbModel(heartbeatCfg?.model ?? "");
    setHbPrompt(heartbeatCfg?.prompt ?? "");
  }, [heartbeatCfg?.enabled, heartbeatCfg?.intervalMinutes, heartbeatCfg?.model, heartbeatCfg?.prompt]);

  const heartbeatMutation = useMutation({
    mutationFn: () => {
      const existing = (agent?.adapterConfig as Record<string, unknown>) ?? {};
      return agentsApi.update(agentId, {
        adapterConfig: {
          ...existing,
          heartbeat: {
            enabled: hbEnabled,
            intervalMinutes: hbInterval,
            ...(hbModel ? { model: hbModel } : {}),
            ...(hbPrompt.trim() ? { prompt: hbPrompt.trim() } : {}),
          },
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agentId) });
    },
  });

  const heartbeatDirty =
    hbEnabled !== (heartbeatCfg?.enabled === true) ||
    hbInterval !== (heartbeatCfg?.intervalMinutes ?? 30) ||
    hbModel !== (heartbeatCfg?.model ?? "") ||
    hbPrompt !== (heartbeatCfg?.prompt ?? "");

  // Group models by provider for the selector
  const modelsByProvider = enrichedModels.reduce<Record<string, typeof enrichedModels>>((acc, m) => {
    const key = m.provider;
    if (!acc[key]) acc[key] = [];
    acc[key].push(m);
    return acc;
  }, {});

  const providerDisplayNames: Record<string, string> = {
    anthropic: "Anthropic (Claude)",
    openai: "OpenAI",
    azure_foundry: "Azure AI Foundry",
    google: "Google (Gemini)",
    groq: "Groq",
  };

  // Count total effective tools across all MCPs
  const totalTools = agentMcps.reduce((sum, mcp) => {
    const available = getAvailableTools(mcp.tenantMcpId);
    if (mcp.allowedTools) return sum + mcp.allowedTools.length;
    return sum + available.length;
  }, 0);

  // Avatar controls, rendered inside the Agent Identity card as an optional
  // "generate or upload an avatar" affordance rather than a separate card.
  const avatarControls = (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploadAvatar.isPending || generateAvatar.isPending}
        aria-label="Upload custom avatar"
        className="group relative h-14 w-14 shrink-0 overflow-hidden rounded-full ring-2 ring-border transition-all hover:ring-accent focus:outline-none focus-visible:ring-accent disabled:cursor-wait disabled:opacity-60"
      >
        {agent?.avatarUrl ? (
          <img src={agent.avatarUrl} alt={agent.name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-surface-hover text-text-muted">
            <Image size={22} />
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100">
          {uploadAvatar.isPending ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Upload size={16} />
          )}
        </div>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleAvatarFile(file);
          e.target.value = "";
        }}
      />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={uploadAvatar.isPending || generateAvatar.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploadAvatar.isPending ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <Upload size={14} className="mr-1.5" />
            )}
            Upload
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={generateAvatar.isPending || uploadAvatar.isPending}
            onClick={() => generateAvatar.mutate()}
          >
            {generateAvatar.isPending ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <RefreshCw size={14} className="mr-1.5" />
            )}
            {agent?.avatarUrl ? "Regenerate" : "Generate"}
          </Button>
        </div>
        {uploadAvatar.isSuccess && (
          <p className="flex items-center gap-1.5 text-xs text-success">
            <CheckCircle2 size={12} />
            Avatar uploaded
          </p>
        )}
        {uploadAvatar.isError && (
          <p className="flex items-center gap-1.5 text-xs text-danger">
            <XCircle size={12} />
            {(uploadAvatar.error as Error)?.message || "Upload failed"}
          </p>
        )}
        {generateAvatar.isSuccess && (
          <p className="flex items-center gap-1.5 text-xs text-success">
            <CheckCircle2 size={12} />
            Avatar generated
          </p>
        )}
        {generateAvatar.isError && (
          <p className="flex items-center gap-1.5 text-xs text-danger">
            <XCircle size={12} />
            {(generateAvatar.error as Error)?.message || "Generation failed"}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Agent Identity — name, role/title, seniority, department, responsibilities.
            The avatar controls render inside this card (see avatarControls). */}
        {agent && <AgentIdentityCard agent={agent} agents={allAgents} avatar={avatarControls} />}

        {/* Model Selection */}
        <Card data-tour="model-config">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Cpu size={18} />
                Model
              </CardTitle>
              {/* One button for the whole card. The image model used to carry
                  its own Save, which meant two buttons doing the same job a few
                  rows apart.

                  It still fires only the mutations that are actually dirty:
                  the main/backup model is pushed to the VM and verified, the
                  image model is a platform-side write, so saving one must never
                  drag the other along. */}
              <Button
                size="sm"
                disabled={
                  !modelCardDirty ||
                  modelCardSaving ||
                  (modelSelectionDirty && !selectedModel)
                }
                onClick={() => {
                  if (modelSelectionDirty) syncModelMutation.mutate(selectedModel);
                  if (imageModelDirty) imageModelMutation.mutate();
                }}
              >
                {modelCardSaving ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Send size={14} className="mr-1.5" />
                )}
                {modelCardDirty ? "Update & Sync" : "Synced"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Grouped model selector — searchable.

                The "Running" badge sits inside this field rather than in a row
                of its own above it: that row named the same model a second time,
                so the card showed one model twice.

                It appears only while the picked model IS the one on the agent.
                Pick a different one and it disappears, because the agent is not
                running that yet — the header button is already saying
                "Update & Sync" at that point. */}
            {/* The three field labels use the same treatment as the Agent
                Identity card above: text-xs, font-medium, text-secondary. */}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">Main model</label>
              <ModelPicker
                value={selectedModel}
                onChange={setSelectedModel}
                modelsByProvider={modelsByProvider}
                providers={providers}
                providerDisplayNames={providerDisplayNames}
                emptyLabel="Select a model..."
                showProviderStatus
                statusSlot={
                  selectedModel && selectedModel === currentModel && agent?.status === "active" ? (
                    <Badge variant="success" className="shrink-0">
                      <CheckCircle2 size={10} className="mr-1" />
                      Running
                    </Badge>
                  ) : null
                }
              />
            </div>

            {/* Backup model selector — overrides the platform default backup */}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                Backup model (optional, overrides the platform default)
              </label>
              <ModelPicker
                value={selectedBackup}
                onChange={setSelectedBackup}
                modelsByProvider={modelsByProvider}
                providerDisplayNames={providerDisplayNames}
                emptyLabel="— Use platform default —"
              />
            </div>

            {/* Provider status summary removed — the model picker already shows
                each provider's configured/not-configured state (the ✅/❌ in its
                group headers), so the separate chip row was redundant. */}

            {/* Image model — the agent's generate_image tool. A third model
                choice, so it is a third field rather than a sub-section with a
                heading, a divider and a Save of its own, all of which repeated
                what the card already said.

                Same picker as the two above on purpose. Unlike them, nothing is
                pushed to the VM and no restart is involved. */}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                Image model (used by this agent&apos;s{" "}
                <span className="font-mono">generate_image</span> tool)
              </label>
              {imageModels.length === 0 ? (
                <p className="text-xs text-text-muted">
                  No image models are available to this tenant yet. A platform admin can grant one
                  in Admin → Model Catalogue, after importing it from an Azure Foundry connection.
                </p>
              ) : (
                <>
                  <ModelPicker
                    value={selectedImageModel}
                    onChange={setSelectedImageModel}
                    modelsByProvider={imageModelsByProvider}
                    providerDisplayNames={providerDisplayNames}
                    emptyLabel="— Use tenant default —"
                  />
                  {resolvedImageModel && (
                    <p className="pt-1 text-xs text-text-muted">
                      Currently resolves to{" "}
                      <span className="font-medium text-text-primary">{resolvedImageModel.displayName}</span>
                      {resolvedImageModel.source === "tenant" && " (tenant default)"}
                      {resolvedImageModel.source === "only-granted" &&
                        " (the only image model granted to this tenant)"}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Provider status summary removed — the model picker already shows
                each provider's configured/not-configured state (the ✅/❌ in its
                group headers), so the separate chip row was redundant. */}

            {/* One result row for the whole card, below all three fields.

                The button saves two different things through two endpoints, so
                the outcome can be mixed: the machine push can fail while the
                image model saves. Both land here, one under the other, rather
                than in two places the eye has to hunt for. */}
            <div className="flex flex-col items-start gap-1.5 empty:hidden">
              {syncModelMutation.isPending && (
                <span className="text-xs text-text-muted">
                  <Loader2 size={12} className="inline mr-1 animate-spin" />
                  Applying &amp; verifying the model on the agent…
                </span>
              )}
              {syncModelMutation.isSuccess &&
                (() => {
                  const d = syncModelMutation.data;
                  // verified === true → live-probed and serving; false → config
                  // written but the agent couldn't actually serve it (bad creds /
                  // unknown provider); undefined → adapter doesn't probe (OpenClaw).
                  if (d?.verified === true) {
                    return (
                      <span className="text-xs font-medium text-success">
                        <CheckCircle2 size={12} className="inline mr-1" />
                        Confirmed: agent is now running {d.activeModel ?? "the selected model"}
                      </span>
                    );
                  }
                  if (d?.verified === false) {
                    return (
                      <span className="text-xs font-medium text-warning">
                        <AlertCircle size={12} className="inline mr-1" />
                        Saved, but the agent could not serve this model{d.error ? `: ${d.error}` : ""}. It may be
                        falling back — verify before relying on it.
                      </span>
                    );
                  }
                  return (
                    <span className="text-xs text-success">
                      <CheckCircle2 size={12} className="inline mr-1" />
                      Model updated{d?.synced ? " and synced to agent" : ""}
                    </span>
                  );
                })()}
              {syncModelMutation.isError && (
                <span className="text-xs text-danger">
                  <XCircle size={12} className="inline mr-1" />
                  Failed to update model
                </span>
              )}
              {imageModelMutation.isPending && (
                <span className="text-xs text-text-muted">
                  <Loader2 size={12} className="inline mr-1 animate-spin" />
                  Saving the image model…
                </span>
              )}
              {imageModelMutation.isSuccess && (
                <span className="text-xs text-success">
                  <CheckCircle2 size={12} className="inline mr-1" />
                  Image model updated, takes effect on the next call with no restart needed
                </span>
              )}
              {imageModelMutation.isError && (
                <span className="text-xs text-danger">
                  <XCircle size={12} className="inline mr-1" />
                  {(imageModelMutation.error as Error)?.message || "Failed to update image model"}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Heartbeat — Native agents only. Schedules a periodic isolated
            check-in turn so the agent surfaces stalled work without the
            user having to prompt. OpenClaw runs its own heartbeat on the
            VM so we hide this card for those. */}
        {isNativeAgent && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <HeartPulse size={18} />
                Heartbeat
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between rounded-lg bg-surface-hover p-2.5">
                <div>
                  <div className="text-sm font-medium text-text-primary">Enable scheduled heartbeats</div>
                  <div className="text-xs text-text-muted">
                    Runs an isolated turn on the agent every {hbInterval} min to surface anything pending or blocked.
                  </div>
                </div>
                <label className="inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={hbEnabled}
                    onChange={(e) => setHbEnabled(e.target.checked)}
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-text-muted">Interval (minutes)</label>
                  <input
                    type="number"
                    min={5}
                    max={1440}
                    value={hbInterval}
                    onChange={(e) => setHbInterval(Math.max(5, Math.min(1440, Number(e.target.value) || 30)))}
                    disabled={!hbEnabled}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-text-muted">Heartbeat model</label>
                  <select
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
                    value={hbModel}
                    onChange={(e) => setHbModel(e.target.value)}
                    disabled={!hbEnabled}
                  >
                    <option value="">Use agent default ({currentModel || "unset"})</option>
                    {Object.entries(modelsByProvider).map(([provider, models]) => {
                      const access = providers[provider];
                      const readyIcon = access?.ready ? "\u2705" : "\u274C";
                      const groupLabel = `${readyIcon} ${providerDisplayNames[provider] || provider}`;
                      return (
                        <optgroup key={provider} label={groupLabel}>
                          {models.map((m) => (
                            <option key={m.id} value={m.modelId} disabled={!m.accessReady}>
                              {m.displayName}{!m.accessReady ? " (not available)" : ""}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-text-muted">
                  Custom prompt (optional)
                </label>
                <textarea
                  rows={3}
                  value={hbPrompt}
                  onChange={(e) => setHbPrompt(e.target.value)}
                  disabled={!hbEnabled}
                  placeholder="Default: Read HEARTBEAT.md if it exists, surface anything blocked, otherwise reply HEARTBEAT_OK."
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
                />
              </div>

              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  disabled={!heartbeatDirty || heartbeatMutation.isPending}
                  onClick={() => heartbeatMutation.mutate()}
                >
                  {heartbeatMutation.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Save size={14} className="mr-1.5" />
                  )}
                  Save
                </Button>
                {heartbeatMutation.isSuccess && !heartbeatDirty && (
                  <span className="text-xs text-success">
                    <CheckCircle2 size={12} className="inline mr-1" />
                    Saved
                  </span>
                )}
                {heartbeatMutation.isError && (
                  <span className="text-xs text-danger">
                    <XCircle size={12} className="inline mr-1" />
                    Failed to save
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Workspace Files */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText size={18} />
                Workspace Files
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={regenerateWorkspace.isPending}
                  onClick={() => regenerateWorkspace.mutate()}
                >
                  {regenerateWorkspace.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw size={14} className="mr-1.5" />
                  )}
                  Regenerate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pushWorkspace.isPending}
                  onClick={() => pushWorkspace.mutate()}
                >
                  {pushWorkspace.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Send size={14} className="mr-1.5" />
                  )}
                  Push to Agent
                </Button>
                <Button
                  size="sm"
                  disabled={syncWorkspace.isPending}
                  onClick={() => syncWorkspace.mutate()}
                >
                  {syncWorkspace.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Zap size={14} className="mr-1.5" />
                  )}
                  Update Context
                </Button>
              </div>
            </div>
            {workspace?.generatedAt && (
              <p className="mt-1 text-xs text-text-muted">
                Generated: {new Date(workspace.generatedAt).toLocaleString()}
              </p>
            )}
            {workspace?.updatedAt && (
              <p className="text-xs text-text-muted">
                Last updated: {new Date(workspace.updatedAt).toLocaleString()}
              </p>
            )}
            {syncWorkspace.isSuccess && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-success">
                <CheckCircle2 size={12} />
                Context updated and pushed to agent
              </p>
            )}
            {syncWorkspace.isError && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-danger">
                <XCircle size={12} />
                {(syncWorkspace.error as Error)?.message || "Sync failed"}
              </p>
            )}
            {pushWorkspace.isSuccess && !syncWorkspace.isSuccess && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-success">
                <CheckCircle2 size={12} />
                Workspace pushed to agent
              </p>
            )}
            {pushWorkspace.isError && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-danger">
                <XCircle size={12} />
                {(pushWorkspace.error as Error)?.message || "Push failed"}
              </p>
            )}
          </CardHeader>
          <CardContent>
            {!workspace ? (
              <div className="text-center py-6">
                <FileText size={32} className="mx-auto mb-2 text-text-muted" />
                <p className="text-sm text-text-muted">
                  No workspace files generated yet.
                </p>
                <Button
                  size="sm"
                  className="mt-3"
                  disabled={regenerateWorkspace.isPending}
                  onClick={() => regenerateWorkspace.mutate()}
                >
                  {regenerateWorkspace.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw size={14} className="mr-1.5" />
                  )}
                  Generate Workspace
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {WORKSPACE_FILES.map(({ key, label }) => {
                  const isExpanded = expandedFiles.has(key);
                  const isEditing = editingFiles.has(key);
                  const content = workspace[key] || "";
                  const hasContent = content.length > 0;

                  return (
                    <div key={key} className="rounded border border-border">
                      <button
                        className="flex w-full items-center justify-between px-3 py-2.5 text-left hover:bg-surface-hover transition-colors"
                        onClick={() => toggleExpand(key)}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown size={14} className="text-text-muted" />
                          ) : (
                            <ChevronRight size={14} className="text-text-muted" />
                          )}
                          <FileText size={14} className="text-accent" />
                          <span className="text-sm font-medium text-text-primary font-mono">
                            {label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {hasContent ? (
                            <Badge variant="success">
                              {content.length} chars
                            </Badge>
                          ) : (
                            <Badge variant="default">empty</Badge>
                          )}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-border px-3 py-3">
                          <div className="mb-2 flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); toggleEdit(key); }}
                            >
                              <Pencil size={12} className="mr-1" />
                              {isEditing ? "Cancel" : "Edit"}
                            </Button>
                            {isEditing && (
                              <Button
                                size="sm"
                                disabled={saveWorkspaceFile.isPending}
                                onClick={(e) => { e.stopPropagation(); saveWorkspaceFile.mutate(key); }}
                              >
                                <Save size={12} className="mr-1" />
                                Save
                              </Button>
                            )}
                          </div>
                          {isEditing ? (
                            <textarea
                              value={editedContent[key] ?? ""}
                              onChange={(e) =>
                                setEditedContent((prev) => ({ ...prev, [key]: e.target.value }))
                              }
                              rows={10}
                              className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-text-primary resize-y min-h-[200px]"
                            />
                          ) : (
                            <pre className="whitespace-pre-wrap rounded-md bg-surface-hover p-3 font-mono text-xs text-text-secondary max-h-[400px] overflow-y-auto">
                              {content || "(empty)"}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agent MCPs - Enhanced with tool toggles */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Puzzle size={18} />
                MCP Servers ({agentMcps.length})
                {totalTools > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    <Wrench size={10} className="mr-1" />
                    {totalTools} tools
                  </Badge>
                )}
              </CardTitle>
              <div className="flex gap-1.5">
                {/* Only an MCP already in the tenant pool can be assigned here.
                    Creating one is a tenant-level job, so this opens the Tenant
                    Settings dialog on MCP Servers with its create form ready.

                    The dialog has no address of its own, so this window event
                    is the only way in — the same one the guided tour and Scout
                    use (`tenant-shell.tsx`). Going to /settings/mcp instead
                    would drop the user on the full-page copy of the same
                    screen, which is the second front door, not this one. */}
                {isTenantAdmin && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      window.dispatchEvent(
                        new CustomEvent("proclaw:open-settings", {
                          detail: { kind: "tenant", section: "mcp", create: true },
                        }),
                      )
                    }
                  >
                    <Plus size={14} className="mr-1.5" />
                    Add MCP
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={discoverMutation.isPending}
                  onClick={() => discoverMutation.mutate()}
                >
                  {discoverMutation.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Search size={14} className="mr-1.5" />
                  )}
                  Discover
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={agentMcps.length === 0 || syncMutation.isPending}
                  onClick={() => syncMutation.mutate()}
                >
                  {syncMutation.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Upload size={14} className="mr-1.5" />
                  )}
                  Sync to Agent
                </Button>
              </div>
            </div>
            {syncMutation.data && (
              <div className={`mt-2 flex items-center gap-2 rounded-lg p-2 text-xs ${
                syncMutation.data.success ? "bg-success/5 text-success" : "bg-danger/5 text-danger"
              }`}>
                {syncMutation.data.success ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                {syncMutation.data.success ? (
                  <span>
                    Synced successfully
                    {syncMutation.data.serverCount != null && ` · ${syncMutation.data.serverCount} server${syncMutation.data.serverCount !== 1 ? "s" : ""} pushed`}
                    {syncMutation.data.added && syncMutation.data.added.length > 0 && ` · +${syncMutation.data.added.length} added`}
                    {syncMutation.data.updated && syncMutation.data.updated.length > 0 && ` · ${syncMutation.data.updated.length} updated`}
                  </span>
                ) : (syncMutation.data.error || "Sync failed")}
              </div>
            )}
            {syncMutation.isError && (
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-danger/5 p-2 text-xs text-danger">
                <XCircle size={14} />
                {(syncMutation.error as Error)?.message || "Sync failed"}
              </div>
            )}
            {discoverMutation.data && (
              <div className={`mt-2 flex items-center gap-2 rounded-lg p-2 text-xs ${
                discoverMutation.data.error ? "bg-danger/5 text-danger" : "bg-warning/5 text-warning"
              }`}>
                {discoverMutation.data.error ? (
                  <><XCircle size={14} />{discoverMutation.data.error}</>
                ) : (
                  <><Search size={14} />
                    {discoverMutation.data.discovered.length} agent-created
                    {discoverMutation.data.platformManaged.length > 0 && ` · ${discoverMutation.data.platformManaged.length} platform-managed`}
                    {discoverMutation.data.removed.length > 0 && ` · ${discoverMutation.data.removed.length} removed`}
                  </>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {/* Assign dropdown */}
            {availableMcps.length > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <select
                  value={selectedMcp}
                  onChange={(e) => setSelectedMcp(e.target.value)}
                  className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  <option value="">Assign MCP from tenant pool...</option>
                  {availableMcps.map((t) => {
                    const cat = t.catalogueMcpId ? catalogueMap.get(t.catalogueMcpId) : null;
                    return (
                      <option key={t.id} value={t.id}>
                        {t.name}
                        {cat ? ` (${cat.transportType.toUpperCase()})` : ""}
                      </option>
                    );
                  })}
                </select>
                <Button size="sm" disabled={!selectedMcp || assignMcp.isPending} onClick={() => assignMcp.mutate(selectedMcp)}>
                  <Plus size={14} className="mr-1" />
                  Assign
                </Button>
              </div>
            )}

            {tenantMcps.length === 0 ? (
              <p className="text-sm text-text-muted">No MCPs available. Assign MCPs to this tenant first in Tenant Settings.</p>
            ) : agentMcps.length === 0 ? (
              <p className="text-sm text-text-muted">No MCPs assigned to this agent.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {agentMcps.map((mcp) => {
                  const tenantMcp = tenantMcpMap.get(mcp.tenantMcpId);
                  const cat = getTenantMcpCatalogue(mcp.tenantMcpId);
                  const isExpanded = expandedMcpId === mcp.id;
                  const availableTools = getAvailableTools(mcp.tenantMcpId);
                  const currentFilter = agentToolFilter[mcp.id] || new Set(availableTools);
                  const hasToolRestriction = mcp.allowedTools !== null && mcp.allowedTools !== undefined;
                  const Icon = cat ? (transportIcons[cat.transportType] || Globe) : Puzzle;
                  const enabledToolCount = hasToolRestriction ? mcp.allowedTools!.length : availableTools.length;

                  return (
                    <div key={mcp.id} className="rounded border border-border">
                      <button
                        className="flex w-full items-center justify-between px-3 py-2.5 text-left hover:bg-surface-hover transition-colors"
                        onClick={() => setExpandedMcpId(isExpanded ? null : mcp.id)}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown size={14} className="text-text-muted" /> : <ChevronRight size={14} className="text-text-muted" />}
                          <Icon size={14} className="text-accent" />
                          <span className="text-sm font-medium text-text-primary">
                            {mcpNameMap.get(mcp.tenantMcpId) ?? mcp.tenantMcpId.slice(0, 8)}
                          </span>
                          {cat && (
                            <span className="text-xs text-text-muted">({cat.transportType.toUpperCase()})</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {availableTools.length > 0 && (
                            <Badge variant="secondary">
                              <Wrench size={10} className="mr-1" />
                              {enabledToolCount}/{availableTools.length} tools
                            </Badge>
                          )}
                          <Badge variant={mcp.enabled ? "success" : "default"}>
                            {mcp.enabled ? "enabled" : "disabled"}
                          </Badge>
                          {cat && (cat.transportType === "http" || cat.transportType === "sse") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={discoveringToolsFor === mcp.id}
                              title="Re-probe the server for its current tool list"
                              onClick={(e) => {
                                e.stopPropagation();
                                discoverToolsForAgentMcp.mutate({ agentMcpId: mcp.id, catalogueId: cat.id });
                              }}
                            >
                              {discoveringToolsFor === mcp.id ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <RefreshCw size={14} />
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAgentMcp.mutate({ mcpId: mcp.id, enabled: !mcp.enabled });
                            }}
                          >
                            <Power size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); removeMcp.mutate(mcp.id); }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </button>

                      {/* Expanded: per-tool toggles */}
                      {isExpanded && (
                        <div className="border-t border-border px-3 py-3 space-y-3">
                          {toolDiscoveryResult[mcp.id] && (
                            <div className={`flex items-center gap-2 rounded-lg p-2 text-xs ${
                              toolDiscoveryResult[mcp.id].success
                                ? "bg-success/5 text-success"
                                : "bg-danger/5 text-danger"
                            }`}>
                              {toolDiscoveryResult[mcp.id].success ? (
                                <>
                                  <CheckCircle2 size={14} />
                                  Refreshed · {toolDiscoveryResult[mcp.id].count ?? 0} tools discovered
                                </>
                              ) : (
                                <>
                                  <XCircle size={14} />
                                  {toolDiscoveryResult[mcp.id].error ?? "Failed to discover tools"}
                                </>
                              )}
                            </div>
                          )}
                          {availableTools.length > 0 ? (
                            <>
                              <div className="flex items-center justify-between">
                                <p className="text-xs font-medium text-text-secondary">
                                  Tool Selection
                                  <span className="ml-1.5 font-normal text-text-muted">
                                    (controls which tools are available to this agent)
                                  </span>
                                </p>
                                <div className="flex items-center gap-2">
                                  <button
                                    className="text-[11px] text-accent hover:underline"
                                    onClick={() => setAgentToolFilter((prev) => ({
                                      ...prev,
                                      [mcp.id]: new Set(availableTools),
                                    }))}
                                  >
                                    All
                                  </button>
                                  <span className="text-text-muted">|</span>
                                  <button
                                    className="text-[11px] text-accent hover:underline"
                                    onClick={() => setAgentToolFilter((prev) => ({
                                      ...prev,
                                      [mcp.id]: new Set(),
                                    }))}
                                  >
                                    None
                                  </button>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 gap-1">
                                {availableTools.map((toolName) => {
                                  const isEnabled = currentFilter.has(toolName);
                                  const desc = getToolDescription(mcp.tenantMcpId, toolName);
                                  return (
                                    <label
                                      key={toolName}
                                      className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-surface-hover"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isEnabled}
                                        onChange={() => toggleAgentTool(mcp.id, toolName, availableTools)}
                                        className="h-3.5 w-3.5 rounded border-border accent-accent"
                                      />
                                      <div className="min-w-0 flex-1">
                                        <span className="text-xs font-mono font-medium text-text-primary">
                                          {toolName}
                                        </span>
                                        {desc && (
                                          <p className="text-[10px] text-text-muted truncate">{desc}</p>
                                        )}
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>

                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  disabled={updateAgentMcpTools.isPending}
                                  onClick={() => saveAgentToolFilter(mcp.id, availableTools)}
                                >
                                  Save Tool Selection
                                </Button>
                                <span className="text-[11px] text-text-muted">
                                  {currentFilter.size} of {availableTools.length} tools enabled
                                </span>
                              </div>
                            </>
                          ) : (
                            <p className="text-xs text-text-muted">
                              No tools discovered for this MCP. Test the connection in the MCP Catalogue to discover tools.
                            </p>
                          )}

                          {/* Config overrides info */}
                          {mcp.configOverrides && Object.keys(mcp.configOverrides).length > 0 && (
                            <div className="mt-2 rounded bg-surface-hover p-2">
                              <span className="text-[11px] text-text-muted">Config overrides: </span>
                              <span className="text-[11px] font-mono text-text-secondary">
                                {Object.keys(mcp.configOverrides).join(", ")}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Discovered MCPs (agent-created, not platform-managed) */}
            {discoveredMcps.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Search size={14} className="text-warning" />
                  <span className="text-xs font-medium text-text-muted uppercase">
                    Discovered on Agent ({discoveredMcps.length})
                  </span>
                </div>
                {discoveredMcps.map((dm) => {
                  const TransportIcon = transportIcons[dm.transportType || "http"] || Globe;
                  const cfg = dm.config || {};
                  return (
                    <div
                      key={dm.id}
                      className="rounded-lg border border-dashed border-warning/40 bg-warning/5 p-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <TransportIcon size={14} className="text-warning" />
                          <span className="text-sm font-medium">{dm.name}</span>
                          <Badge variant="outline" className="text-[10px] border-warning/40 text-warning bg-warning/10">
                            Agent-created
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => {
                              setPromoteTarget(dm);
                              setPromoteAgentIds(new Set());
                            }}
                          >
                            <ArrowUpRight size={12} className="mr-1" />
                            Promote
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-danger hover:text-danger hover:bg-danger/10"
                            onClick={() => removeDiscoveredMcp.mutate(dm.id)}
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-1.5 text-[11px] text-text-muted">
                        {cfg.url ? <span className="mr-3">URL: {String(cfg.url)}</span> : null}
                        {cfg.command ? <span className="mr-3">Command: {String(cfg.command)}</span> : null}
                        {cfg.transport ? <span>Transport: {String(cfg.transport)}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Promote dialog */}
        {promoteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPromoteTarget(null)}>
            <div className="w-[min(440px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-sm font-semibold text-text-primary mb-1">Promote to Tenant Catalogue</h3>
              <p className="text-xs text-text-secondary mb-3">
                Promote <span className="font-medium text-text-primary">"{promoteTarget.name}"</span> to the tenant MCP catalogue so it can be managed and assigned to other agents.
              </p>

              <div className="rounded-lg border border-border bg-surface-hover p-3 mb-3 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-text-muted">Transport:</span>
                  <span className="font-medium">{promoteTarget.transportType || "http"}</span>
                </div>
                {promoteTarget.config?.url ? (
                  <div className="flex items-center gap-2">
                    <span className="text-text-muted">URL:</span>
                    <span className="font-mono text-[11px]">{String(promoteTarget.config.url)}</span>
                  </div>
                ) : null}
                {promoteTarget.config?.command ? (
                  <div className="flex items-center gap-2">
                    <span className="text-text-muted">Command:</span>
                    <span className="font-mono text-[11px]">{String(promoteTarget.config.command)}</span>
                  </div>
                ) : null}
              </div>

              {/* Optional: assign to other agents */}
              {allAgents.filter((a) => a.id !== agentId && a.status === "active").length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-text-secondary mb-1.5">Also deploy to other agents (optional):</p>
                  <div className="max-h-32 overflow-y-auto space-y-1 rounded-lg border border-border p-2">
                    {allAgents
                      .filter((a) => a.id !== agentId && a.status === "active")
                      .map((a) => (
                        <label key={a.id} className="flex items-center gap-2 cursor-pointer text-xs hover:bg-surface-hover rounded px-1 py-0.5">
                          <input
                            type="checkbox"
                            checked={promoteAgentIds.has(a.id)}
                            onChange={() => {
                              setPromoteAgentIds((prev) => {
                                const next = new Set(prev);
                                next.has(a.id) ? next.delete(a.id) : next.add(a.id);
                                return next;
                              });
                            }}
                            className="rounded border-border"
                          />
                          <span className="truncate">{a.name}</span>
                          <span className="ml-auto text-text-muted">{a.role}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setPromoteTarget(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={promoteMutation.isPending}
                  onClick={() => {
                    const targets = promoteAgentIds.size > 0 ? Array.from(promoteAgentIds) : undefined;
                    promoteMutation.mutate({ id: promoteTarget.id, targetAgentIds: targets });
                  }}
                >
                  {promoteMutation.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <ArrowUpRight size={14} className="mr-1.5" />
                  )}
                  Promote{promoteAgentIds.size > 0 ? ` & Deploy to ${promoteAgentIds.size}` : ""}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Agent Skills */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Zap size={18} />
                Skills ({agentSkills.length})
                {discoveredSkills.length > 0 && (
                  <Badge variant="secondary" className="ml-1 bg-warning/10 text-warning border-warning/30">
                    +{discoveredSkills.length} discovered
                  </Badge>
                )}
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                disabled={discoverSkillsMutation.isPending}
                onClick={() => discoverSkillsMutation.mutate()}
              >
                {discoverSkillsMutation.isPending ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Search size={14} className="mr-1.5" />
                )}
                Discover
              </Button>
            </div>
            {discoverSkillsMutation.data && (
              <div className={`mt-2 flex items-center gap-2 rounded-lg p-2 text-xs ${
                discoverSkillsMutation.data.error ? "bg-danger/5 text-danger" : "bg-warning/5 text-warning"
              }`}>
                {discoverSkillsMutation.data.error ? (
                  <><XCircle size={14} />{discoverSkillsMutation.data.error}</>
                ) : (
                  <><Search size={14} />
                    {discoverSkillsMutation.data.discovered.length} agent-created
                    {discoverSkillsMutation.data.platformManaged.length > 0 && ` · ${discoverSkillsMutation.data.platformManaged.length} platform-managed`}
                  </>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {availableSkills.length > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <select
                  value={selectedSkill}
                  onChange={(e) => setSelectedSkill(e.target.value)}
                  className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  <option value="">Assign Skill from tenant pool...</option>
                  {availableSkills.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <Button size="sm" disabled={!selectedSkill || assignSkill.isPending} onClick={() => assignSkill.mutate(selectedSkill)}>
                  <Plus size={14} className="mr-1" />
                  Assign
                </Button>
              </div>
            )}

            {tenantSkills.length === 0 ? (
              <p className="text-sm text-text-muted">No skills available. Assign skills to this tenant first in Tenant Settings.</p>
            ) : agentSkills.length === 0 ? (
              <p className="text-sm text-text-muted">No skills assigned to this agent.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {agentSkills.map((skill) => (
                  <div key={skill.id} className="rounded border border-border px-3 py-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap size={14} className="text-accent" />
                        <span className="text-sm font-medium text-text-primary">{skillNameMap.get(skill.tenantSkillId) ?? skill.tenantSkillId.slice(0, 8)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={skill.enabled ? "success" : "default"}>
                          {skill.enabled ? "enabled" : "disabled"}
                        </Badge>
                        <Button size="sm" variant="ghost" onClick={() => removeSkill.mutate(skill.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                    {skill.parameterOverrides && Object.keys(skill.parameterOverrides).length > 0 && (
                      <div className="mt-1.5">
                        <span className="text-[11px] text-text-muted">Parameter overrides: </span>
                        <span className="text-[11px] font-mono text-text-secondary">
                          {Object.entries(skill.parameterOverrides).map(([k, v]) => `${k}=${String(v)}`).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Discovered Skills (agent-created) */}
            {discoveredSkills.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Search size={14} className="text-warning" />
                  <span className="text-xs font-medium text-text-muted uppercase">
                    Discovered on Agent ({discoveredSkills.length})
                  </span>
                </div>
                {discoveredSkills.map((ds) => (
                  <div
                    key={ds.id}
                    className="rounded-lg border border-dashed border-warning/40 bg-warning/5 p-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap size={14} className="text-warning" />
                        <span className="text-sm font-medium">{ds.name}</span>
                        <Badge variant="outline" className="text-[10px] border-warning/40 text-warning bg-warning/10">
                          Agent-created
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => {
                            setPromoteSkillTarget(ds);
                            setPromoteSkillAgentIds(new Set());
                          }}
                        >
                          <ArrowUpRight size={12} className="mr-1" />
                          Promote
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-danger hover:text-danger hover:bg-danger/10"
                          onClick={() => removeDiscoveredSkill.mutate(ds.id)}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                    {ds.description && (
                      <p className="mt-1 text-[11px] text-text-muted">{ds.description}</p>
                    )}
                    <p className="mt-0.5 text-[10px] text-text-muted font-mono">~/.openclaw/skills/{ds.dirName}/</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Promote Skill dialog */}
        {promoteSkillTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPromoteSkillTarget(null)}>
            <div className="w-[min(440px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-sm font-semibold text-text-primary mb-1">Promote Skill to Tenant Catalogue</h3>
              <p className="text-xs text-text-secondary mb-3">
                Promote <span className="font-medium text-text-primary">"{promoteSkillTarget.name}"</span> to the tenant skill catalogue and optionally copy to other agents.
              </p>

              <div className="rounded-lg border border-border bg-surface-hover p-3 mb-3 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-text-muted">Directory:</span>
                  <span className="font-mono text-[11px]">{promoteSkillTarget.dirName}</span>
                </div>
                {promoteSkillTarget.description && (
                  <div className="flex items-start gap-2">
                    <span className="text-text-muted shrink-0">Description:</span>
                    <span className="text-text-secondary">{promoteSkillTarget.description}</span>
                  </div>
                )}
              </div>

              {allAgents.filter((a) => a.id !== agentId && a.status === "active").length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-text-secondary mb-1.5">Also copy to other agents (optional):</p>
                  <div className="max-h-32 overflow-y-auto space-y-1 rounded-lg border border-border p-2">
                    {allAgents
                      .filter((a) => a.id !== agentId && a.status === "active")
                      .map((a) => (
                        <label key={a.id} className="flex items-center gap-2 cursor-pointer text-xs hover:bg-surface-hover rounded px-1 py-0.5">
                          <input
                            type="checkbox"
                            checked={promoteSkillAgentIds.has(a.id)}
                            onChange={() => {
                              setPromoteSkillAgentIds((prev) => {
                                const next = new Set(prev);
                                next.has(a.id) ? next.delete(a.id) : next.add(a.id);
                                return next;
                              });
                            }}
                            className="rounded border-border"
                          />
                          <span className="truncate">{a.name}</span>
                          <span className="ml-auto text-text-muted">{a.role}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setPromoteSkillTarget(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={promoteSkillMutation.isPending}
                  onClick={() => {
                    const targets = promoteSkillAgentIds.size > 0 ? Array.from(promoteSkillAgentIds) : undefined;
                    promoteSkillMutation.mutate({ id: promoteSkillTarget.id, targetAgentIds: targets });
                  }}
                >
                  {promoteSkillMutation.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <ArrowUpRight size={14} className="mr-1.5" />
                  )}
                  Promote{promoteSkillAgentIds.size > 0 ? ` & Copy to ${promoteSkillAgentIds.size}` : ""}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Memory Access */}
        <AgentMemoryAccessTab agentId={agentId} />

        {/* Agent Mailbox */}
        <AgentMailboxCard agentId={agentId} tenantId={tenantId} agent={agent} />

        {/* Agent Secrets (Vault-backed) */}
        <AgentSecretsCard agentId={agentId} />

        {/* Agent API Keys */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Key size={18} />
              API Keys ({apiKeys.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex items-center gap-2">
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Key name (e.g. default)"
                className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
              />
              <Button
                size="sm"
                disabled={!newKeyName.trim() || createKey.isPending}
                onClick={() => createKey.mutate(newKeyName.trim())}
              >
                <Plus size={14} className="mr-1" />
                Create Key
              </Button>
            </div>

            {createdToken && (
              <div className="mb-4 rounded-lg border border-accent/30 bg-accent/5 p-3">
                <p className="mb-1.5 text-xs font-medium text-accent">
                  New API key created. Copy it now -- it will not be shown again.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all rounded bg-background px-2 py-1.5 text-xs font-mono text-text-primary">
                    {createdToken}
                  </code>
                  <Button size="sm" variant="outline" onClick={handleCopyToken}>
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </div>
              </div>
            )}

            {apiKeys.length === 0 ? (
              <p className="text-sm text-text-muted">No API keys for this agent.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {apiKeys.map((key) => (
                  <div key={key.id} className="rounded border border-border px-3 py-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Key size={14} className="text-accent" />
                        <span className="text-sm font-medium text-text-primary">{key.name}</span>
                        <span className="rounded bg-background px-1.5 py-0.5 text-[11px] font-mono text-text-secondary">
                          {key.tokenPrefix}...
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => revokeKey.mutate(key.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-1 flex gap-3 text-[11px] text-text-muted">
                      <span>Created: {new Date(key.createdAt).toLocaleDateString()}</span>
                      {key.lastUsedAt && (
                        <span>Last used: {new Date(key.lastUsedAt).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* What this agent's standing setup costs before it does any work —
            persona, skills and model choice. */}
        <EvalPanel targetType="agent" targetId={agentId} />
      </div>
    </div>
  );
}

// --- Agent Secrets Card (Vault-backed environment variables) ---

function AgentSecretsCard({ agentId }: { agentId: string }) {
  const queryClient = useQueryClient();
  const [newEnvName, setNewEnvName] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newDescription, setNewDescription] = useState("");

  const { data: secrets = [] } = useQuery({
    queryKey: ["agent-secrets", agentId],
    queryFn: () => agentSecretsApi.list(agentId),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      agentSecretsApi.create(agentId, {
        envName: newEnvName.trim(),
        value: newValue,
        description: newDescription.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-secrets", agentId] });
      setNewEnvName("");
      setNewValue("");
      setNewDescription("");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (secretId: string) => agentSecretsApi.remove(agentId, secretId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent-secrets", agentId] }),
  });

  const syncMutation = useMutation({
    mutationFn: () => agentSecretsApi.sync(agentId),
  });

  const canCreate = /^[A-Z][A-Z0-9_]*$/.test(newEnvName.trim()) && newValue.length > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield size={18} />
            Secrets ({secrets.length})
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            disabled={secrets.length === 0 || syncMutation.isPending}
            onClick={() => syncMutation.mutate()}
          >
            {syncMutation.isPending ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <Upload size={14} className="mr-1.5" />
            )}
            Sync Resolver
          </Button>
        </div>
        <p className="text-xs text-text-muted mt-1">
          Environment variables resolved at runtime from Azure Key Vault via managed identity. Never stored on disk.
        </p>
        {syncMutation.data && (
          <div className={`mt-2 flex items-center gap-2 rounded-lg p-2 text-xs ${
            syncMutation.data.success ? "bg-success/5 text-success" : "bg-danger/5 text-danger"
          }`}>
            {syncMutation.data.success ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            {syncMutation.data.success
              ? `Resolver synced with ${syncMutation.data.secretCount} secret${syncMutation.data.secretCount !== 1 ? "s" : ""}`
              : (syncMutation.data.error || "Sync failed")}
          </div>
        )}
        {syncMutation.isError && (
          <div className="mt-2 flex items-center gap-2 rounded-lg bg-danger/5 p-2 text-xs text-danger">
            <XCircle size={14} />
            {(syncMutation.error as Error)?.message || "Sync failed"}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {/* Add new secret */}
        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newEnvName}
              onChange={(e) => setNewEnvName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
              placeholder="ENV_VAR_NAME"
              className="w-[180px] rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono"
            />
            <input
              type="password"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="Secret value"
              className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
            />
            <Button
              size="sm"
              disabled={!canCreate || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              <Plus size={14} className="mr-1" />
              Add
            </Button>
          </div>
          <input
            type="text"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description (optional, e.g. Jira Cloud API token)"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-xs text-text-secondary"
          />
          {createMutation.isError && (
            <p className="text-xs text-danger">{(createMutation.error as Error)?.message || "Failed to store secret"}</p>
          )}
        </div>

        {/* Secret list */}
        {secrets.length === 0 ? (
          <p className="text-sm text-text-muted">No secrets configured. Add environment variables that the agent can resolve from Key Vault at runtime.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {secrets.map((secret) => (
              <div key={secret.id} className="rounded border border-border px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Lock size={14} className="text-accent" />
                    <span className="text-sm font-mono font-medium text-text-primary">{secret.envName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-mono text-accent">
                      {secret.vaultScope}
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => removeMutation.mutate(secret.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                <div className="mt-1 flex gap-3 text-[11px] text-text-muted">
                  {secret.description && <span>{secret.description}</span>}
                  <span className="font-mono">{secret.vaultSecretName}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- Agent Mailbox Card ---
//
// Per-agent shared mailbox lifecycle:
//   1) Admin selects a mailbox from the tenant directory (calls
//      POST /m365/:companyId/agent/:agentId/mailbox).
//   2) "selected_pending_rbac": display the generated EXO RBAC PowerShell
//      and a "Verify scope" button.
//   3) "rbac_verified": green check + re-verify / change buttons.
//   4) "rbac_failed": show the diagnostic and let the admin re-run / re-verify.

function AgentMailboxCard({ agentId, tenantId, agent }: { agentId: string; tenantId: string; agent: Agent | undefined }) {
  const queryClient = useQueryClient();
  const [selectedMailbox, setSelectedMailbox] = useState("");
  const [manualUpn, setManualUpn] = useState("");
  const [scriptOpen, setScriptOpen] = useState(false);
  const [scriptText, setScriptText] = useState<string | null>(null);
  const [scriptError, setScriptError] = useState<string | null>(null);
  const [scriptCopied, setScriptCopied] = useState(false);

  const { data: identity } = useQuery({
    queryKey: ["m365", "oauth-identity", tenantId],
    queryFn: () => m365Api.getOAuthIdentity(tenantId),
  });
  const isOAuthMode = identity?.authMode === "oauth_user";

  const { data: mailboxRow, isLoading: rowLoading } = useQuery({
    queryKey: ["agent-mailbox", agentId],
    queryFn: () => m365Api.getAgentMailbox(tenantId, agentId),
  });

  const { data: mailboxes = [], isLoading: listLoading } = useQuery({
    queryKey: ["m365-mailboxes", tenantId],
    queryFn: () => m365Api.listMailboxes(tenantId),
    enabled: !mailboxRow,
  });

  const setMutation = useMutation({
    mutationFn: () => {
      const mb = mailboxes.find((m) => m.id === selectedMailbox);
      if (!mb) throw new Error("No mailbox selected");
      return m365Api.setAgentMailbox(tenantId, agentId, {
        mailboxUpn: mb.mail,
        mailboxObjectId: mb.id,
        mailboxAlias: aliasFromUpn(mb.mail),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-mailbox", agentId] });
      setScriptText(null);
      setScriptOpen(false);
    },
  });

  /**
   * Manual UPN bind — used when the customer hasn't granted tenant-wide
   * admin consent (oauth_user mode without admin consent), so listMailboxes
   * is unavailable. We use the UPN as the addressing identifier; Graph
   * accepts UPN at /users/{} so the object ID isn't strictly needed.
   */
  const manualSetMutation = useMutation({
    mutationFn: () => {
      const upn = manualUpn.trim();
      if (!upn || !upn.includes("@")) throw new Error("Enter a valid email address");
      return m365Api.setAgentMailbox(tenantId, agentId, {
        mailboxUpn: upn,
        mailboxObjectId: upn,
        mailboxAlias: aliasFromUpn(upn),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-mailbox", agentId] });
      setManualUpn("");
      setScriptText(null);
      setScriptOpen(false);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => m365Api.removeAgentMailbox(tenantId, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-mailbox", agentId] });
      setScriptText(null);
      setScriptOpen(false);
    },
  });

  const verifyMutation = useMutation({
    mutationFn: () => m365Api.verifyAgentMailboxScope(tenantId, agentId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-mailbox", agentId] });
    },
  });

  const showSetupScript = async () => {
    setScriptOpen(true);
    setScriptError(null);
    if (scriptText) return;
    try {
      const text = await m365Api.getAgentMailboxSetupScript(tenantId, agentId);
      setScriptText(text);
    } catch (err) {
      setScriptError((err as Error).message);
    }
  };

  const copyScript = async () => {
    if (!scriptText) return;
    await navigator.clipboard.writeText(scriptText);
    setScriptCopied(true);
    setTimeout(() => setScriptCopied(false), 2000);
  };

  // Legacy migration prompt: agent has metadata.mailbox but no row in the
  // new table yet. Show as informational so the admin knows to re-bind.
  const legacyMailbox = (agent?.metadata as Record<string, unknown>)?.mailbox as
    | { id: string; email: string; displayName?: string }
    | null
    | undefined;
  const showLegacyHint = !mailboxRow && !rowLoading && !!legacyMailbox?.email;

  const sharedMailboxes = mailboxes.filter((m) => m.type === "shared");
  const userMailboxes = mailboxes.filter((m) => m.type === "user");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail size={18} />
          Mailbox
          {mailboxRow?.scopeStatus === "rbac_verified" && (
            <Badge variant="success">verified</Badge>
          )}
          {mailboxRow?.scopeStatus === "selected_pending_rbac" && (
            <Badge variant="warning">awaiting RBAC</Badge>
          )}
          {mailboxRow?.scopeStatus === "rbac_failed" && (
            <Badge variant="danger">verification failed</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rowLoading ? (
          <p className="text-sm text-text-muted">Loading...</p>
        ) : mailboxRow ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-surface-hover p-2.5">
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-accent" />
                <div>
                  <p className="text-sm font-medium text-text-primary">{mailboxRow.mailboxUpn}</p>
                  <p className="text-xs text-text-muted">
                    Alias: {mailboxRow.mailboxAlias}
                    {mailboxRow.scopeVerifiedAt && (
                      <> &middot; Verified {new Date(mailboxRow.scopeVerifiedAt).toLocaleString()}</>
                    )}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-danger"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate()}
              >
                <Trash2 size={14} className="mr-1" />
                Change
              </Button>
            </div>

            {mailboxRow.scopeStatus === "rbac_verified" ? (
              <div className="rounded border border-success/20 bg-success/5 px-3 py-2 text-xs text-success">
                <CheckCircle2 size={12} className="mr-1 inline" />
                {isOAuthMode
                  ? `Verified — agent can read and send from ${mailboxRow.mailboxUpn} via the service identity${identity?.signedInAs ? ` (${identity.signedInAs})` : ""}.`
                  : `Exchange Online RBAC scope is bound to this mailbox. Mail.* permissions are constrained to ${mailboxRow.mailboxUpn} only.`}
              </div>
            ) : (
              <div className="space-y-2 rounded-lg border border-border bg-surface p-3">
                <p className="text-sm font-medium text-text-primary">
                  {mailboxRow.scopeStatus === "rbac_failed"
                    ? isOAuthMode
                      ? "Service identity cannot access this mailbox"
                      : "RBAC scope verification failed"
                    : isOAuthMode
                      ? "Grant Full Access, then verify"
                      : "Lock down access to this mailbox"}
                </p>
                <p className="text-xs text-text-muted">
                  {isOAuthMode ? (
                    <>
                      In the M365 admin centre, grant the service user{" "}
                      <span className="font-mono">{identity?.signedInAs ?? "(your service identity)"}</span>{" "}
                      <strong>Full Access</strong> on{" "}
                      <span className="font-mono">{mailboxRow.mailboxUpn}</span>, then click{" "}
                      <strong>Verify access</strong>. No PowerShell required — Exchange enforces
                      the per-mailbox boundary based on the user's mailbox rights.
                    </>
                  ) : (
                    <>
                      Run the generated PowerShell as a Global Administrator of your M365 tenant.
                      This binds ProClaw's Mail.ReadWrite and Mail.Send permissions to this single
                      mailbox via Exchange Online RBAC for Applications. Microsoft can take up to
                      2 hours to propagate the role-assignment cache.
                    </>
                  )}
                </p>
                {mailboxRow.lastVerificationError && (
                  <p className="rounded bg-danger/10 px-2 py-1 font-mono text-[11px] text-danger">
                    {mailboxRow.lastVerificationError}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {!isOAuthMode && (
                    <Button size="sm" variant="outline" onClick={showSetupScript}>
                      <FileText size={12} className="mr-1.5" />
                      {scriptOpen ? "Hide setup script" : "Show setup script"}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    disabled={verifyMutation.isPending}
                    onClick={() => verifyMutation.mutate()}
                  >
                    {verifyMutation.isPending ? (
                      <Loader2 size={12} className="mr-1.5 animate-spin" />
                    ) : (
                      <CheckCircle2 size={12} className="mr-1.5" />
                    )}
                    {isOAuthMode ? "Verify access" : "Verify scope"}
                  </Button>
                </div>

                {scriptOpen && (
                  <div className="space-y-2">
                    {scriptError && (
                      <p className="text-xs text-danger">{scriptError}</p>
                    )}
                    {scriptText && (
                      <>
                        <div className="flex justify-end">
                          <Button size="sm" variant="ghost" onClick={copyScript}>
                            {scriptCopied ? (
                              <>
                                <Check size={12} className="mr-1.5" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy size={12} className="mr-1.5" />
                                Copy
                              </>
                            )}
                          </Button>
                        </div>
                        <pre className="overflow-x-auto rounded border border-border bg-background p-3 font-mono text-[11px] leading-relaxed text-text-secondary">
                          {scriptText}
                        </pre>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {showLegacyHint && (
              <div className="rounded border border-yellow-500/20 bg-yellow-500/5 px-3 py-2 text-xs text-yellow-400">
                <AlertCircle size={12} className="mr-1 inline" />
                This agent has a legacy mailbox configured ({legacyMailbox?.email}). Re-bind it
                here to apply the new EXO RBAC scope and remove tenant-wide Mail.* access.
              </div>
            )}
            {listLoading ? (
              <p className="text-sm text-text-muted">Loading mailboxes...</p>
            ) : mailboxes.length === 0 ? (
              <>
                <p className="text-xs text-text-muted">
                  {isOAuthMode
                    ? `Type the UPN of the shared mailbox you've granted ${identity?.signedInAs ?? "the service identity"} Full Access on. We address the mailbox by UPN — no admin consent required.`
                    : "Type the UPN of the shared mailbox to bind, or grant tenant-wide admin consent under Advanced below to enable the mailbox picker."}
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
                    placeholder="claude-agent@your-domain.com"
                    value={manualUpn}
                    onChange={(e) => setManualUpn(e.target.value)}
                  />
                  <Button
                    size="sm"
                    disabled={!manualUpn.trim() || manualSetMutation.isPending}
                    onClick={() => manualSetMutation.mutate()}
                  >
                    {manualSetMutation.isPending ? (
                      <Loader2 size={14} className="mr-1.5 animate-spin" />
                    ) : (
                      <Mail size={14} className="mr-1.5" />
                    )}
                    Bind
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-text-muted">
                  {isOAuthMode
                    ? `Pick the shared mailbox this agent should use. ${identity?.signedInAs ?? "The service identity"} must have Full Access on it (granted in M365 admin centre).`
                    : "Select the shared mailbox the customer has provisioned for this agent. After saving, run the EXO PowerShell we generate to constrain ProClaw's Mail.* permissions to that single mailbox."}
                </p>
                <select
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                  value={selectedMailbox}
                  onChange={(e) => setSelectedMailbox(e.target.value)}
                >
                  <option value="">Select a mailbox...</option>
                  {sharedMailboxes.length > 0 && (
                    <optgroup label="Shared Mailboxes (recommended)">
                      {sharedMailboxes.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.mail} ({m.displayName})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {userMailboxes.length > 0 && (
                    <optgroup label="User Mailboxes">
                      {userMailboxes.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.mail} ({m.displayName})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <Button
                  size="sm"
                  disabled={!selectedMailbox || setMutation.isPending}
                  onClick={() => setMutation.mutate()}
                >
                  {setMutation.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Mail size={14} className="mr-1.5" />
                  )}
                  Bind Mailbox
                </Button>
              </>
            )}
          </div>
        )}

        {(setMutation.isError ||
          manualSetMutation.isError ||
          removeMutation.isError ||
          verifyMutation.isError) && (
          <p className="mt-2 text-xs text-danger">
            {(
              (setMutation.error ||
                manualSetMutation.error ||
                removeMutation.error ||
                verifyMutation.error) as Error
            )?.message || "Operation failed"}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** Derive a safe EXO alias from a UPN's local part. */
function aliasFromUpn(upn: string): string {
  const local = upn.split("@")[0] ?? upn;
  return local.replace(/[^A-Za-z0-9._-]+/g, "-").slice(0, 64) || "agent-mailbox";
}

const IDENTITY_ROLES = ["ic", "manager", "ceo", "general"] as const;

function AgentIdentityCard({ agent, agents, avatar }: { agent: Agent; agents: Agent[]; avatar?: ReactNode }) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const [name, setName] = useState(agent.name);
  const [title, setTitle] = useState(agent.title ?? "");
  const [role, setRole] = useState(agent.role);
  const [department, setDepartment] = useState(agent.department ?? "");
  const [reportsTo, setReportsTo] = useState(agent.reportsTo ?? "");
  const [capabilities, setCapabilities] = useState(agent.capabilities ?? "");
  const managerOptions = agents
    .filter((candidate) => candidate.id !== agent.id && candidate.companyId === agent.companyId)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Reset local state when the underlying agent changes (e.g. after refetch).
  useEffect(() => {
    setName(agent.name);
    setTitle(agent.title ?? "");
    setRole(agent.role);
    setDepartment(agent.department ?? "");
    setReportsTo(agent.reportsTo ?? "");
    setCapabilities(agent.capabilities ?? "");
  }, [agent.id, agent.name, agent.title, agent.role, agent.department, agent.reportsTo, agent.capabilities]);

  const dirty =
    name.trim() !== agent.name ||
    title.trim() !== (agent.title ?? "") ||
    role !== agent.role ||
    department.trim() !== (agent.department ?? "") ||
    reportsTo !== (agent.reportsTo ?? "") ||
    capabilities.trim() !== (agent.capabilities ?? "");

  const save = useMutation({
    mutationFn: () =>
      agentsApi.update(agent.id, {
        name: name.trim(),
        title: title.trim() || undefined,
        role,
        department: department.trim() || null,
        reportsTo: reportsTo || null,
        capabilities: capabilities.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agent.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.byCompany(tenantId) });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.error("Failed to update agent identity:", err);
      alert(`Failed to save: ${message}`);
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <UserCog size={18} />
            Agent Identity
          </CardTitle>
          <Button
            size="sm"
            disabled={!dirty || !name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <Save size={14} className="mr-1.5" />
            )}
            Save
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {avatar && (
          <div className="border-b border-border/60 pb-4">
            <label className="mb-2 block text-xs font-medium text-text-secondary">Avatar</label>
            {avatar}
          </div>
        )}
        {/* Name and job title sit on one row: they are the pair that names the
            agent, and they match the Seniority/Department row below. */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-text-secondary">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-text-secondary">Role / Job Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              placeholder="e.g. Head of Talent Acquisition"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-text-secondary">Seniority</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
            >
              {IDENTITY_ROLES.map((r) => (
                <option key={r} value={r}>{r.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-text-secondary">Department</label>
            <input
              type="text"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="e.g. People, Engineering"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
            />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-text-secondary">Reports To</label>
          <select
            value={reportsTo}
            onChange={(e) => setReportsTo(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
          >
            <option value="">No manager</option>
            {managerOptions.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}{candidate.title ? ` - ${candidate.title}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-text-secondary">Core Responsibilities</label>
          <textarea
            value={capabilities}
            onChange={(e) => setCapabilities(e.target.value)}
            placeholder="e.g. Sourcing candidates, interview coordination, onboarding, team culture..."
            className="min-h-[80px] w-full resize-y rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
          />
          <p className="mt-1 text-xs text-text-muted">
            Feeds the agent's identity and instructions. Regenerate workspace files below to apply changes.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
